import React from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { CustomTooltip } from '../../keywordResearch/CustomTooltip';
import { TrackYourAIVisibilityCSS as css } from '../style';

interface FeatureRowProps {
  /** Feature key identifier. */
  id: string;
  /** Display title. */
  title: string;
  /** Tooltip description shown on hover. */
  tooltip: string;
  /** Whether this feature is currently enabled (controls border + collapse). */
  isEnabled: boolean;
  /** Called when the Enable toggle is clicked. */
  onToggle: () => void;
  /** When true, the toggle is non-interactive (already enabled in a re-audit). */
  disabled?: boolean;
  /** Optional badge shown next to the title. */
  extraBadge?: React.ReactNode;
  /** Optional warning message shown when configured to another site or disabled. */
  warningMessage?: React.ReactNode;
  /** Force the collapsible section to stay open (e.g. when displaying a disabled configured profile). */
  forceExpanded?: boolean;
  /** Content revealed inside the Collapse when enabled. */
  children?: React.ReactNode;
}

/**
 * A feature toggle row with an Enable button and a collapsible content area.
 * Extracted from OrbitAiStep's `renderFeatureRow` inline function.
 */
export const FeatureRow: React.FC<FeatureRowProps> = ({
  id,
  title,
  tooltip,
  isEnabled,
  onToggle,
  disabled = false,
  extraBadge,
  warningMessage,
  forceExpanded = false,
  children
}) => {
  const isExpanded = isEnabled || forceExpanded;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: isEnabled
          ? 'var(--color-secondary)'
          : warningMessage
          ? '#FECDCA'
          : 'var(--color-border)',
        borderRadius: '8px',
        transition: 'border-color 0.3s ease',
        mb: '8px',
        backgroundColor: disabled ? '#FCFCFD' : '#FFFFFF'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
          py: '8px',
          px: '16px'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexWrap: 'wrap'
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              color: 'var(--color-text-secondary)'
            }}
          >
            {title}
          </Typography>
          <CustomTooltip
            title={
              <Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '18px',
                    textAlign: 'left',
                    color: '#120242'
                  }}
                >
                  {tooltip}
                </Typography>
              </Box>
            }
            placement="top"
            arrow
          >
            <HelpIcon sx={css.helpIcon} />
          </CustomTooltip>
          {extraBadge && (
            <Box
              sx={{
                backgroundColor: '#FEF3F2',
                color: '#B42318',
                border: '1px solid #FECDCA',
                borderRadius: '16px',
                fontSize: '12px',
                py: '2px',
                px: '8px',
                fontWeight: 500,
                lineHeight: '16px'
              }}
            >
              {extraBadge}
            </Box>
          )}
        </Box>
        <Box
          onClick={disabled ? undefined : onToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            pr: '6px',
            pl: '12px',
            py: '6px',
            borderRadius: '20px',
            border: '1px solid',
            borderColor: isEnabled
              ? 'var(--color-secondary)'
              : 'var(--color-border)',
            backgroundColor: isEnabled
              ? 'var(--color-secondary)'
              : 'var(--color-bg-muted-alt)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            userSelect: 'none',
            opacity: disabled ? 0.5 : 1,
            transition: 'border-color 0.3s ease, background-color 0.3s ease'
          }}
        >
          <Box
            sx={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              border: '1px solid var(--color-primary)',
              backgroundColor: isEnabled ? '' : 'transparent',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: isEnabled
                  ? 'var(--color-primary)'
                  : 'transparent'
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: 'var(--color-primary)'
            }}
          >
            {isEnabled ? 'Enabled' : 'Enable'}
          </Typography>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        {children}
        {warningMessage && (
          <Box
            sx={{
              mx: '16px',
              mb: '12px',
              mt: '4px',
              p: '10px 14px',
              borderRadius: '6px',
              backgroundColor: '#FEF3F2',
              border: '1px solid #FECDCA',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 500,
                color: '#B42318',
                lineHeight: '18px'
              }}
            >
              {warningMessage}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
